import React from 'react';
import { apiJson } from '../api';
import { Section } from './ui';

/* ====================================================================
   ADMIN USERS PANEL — manage user roles, view registrations
   ==================================================================== */
function EmailTemplatesPanel({ API, showToast, showConfirm, selectedClubId, isSuperAdmin }) {
  const DEFAULT_RECIPIENT_FILTERS = {
    membershipTypes: [],
    playerTypes: [],
    accountStatuses: [],
    financialState: 'all',
    renewalState: 'all',
    dueSoonDays: 30,
  };

  const [templates, setTemplates] = React.useState([]);
  const [clubMembers, setClubMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editingTemplate, setEditingTemplate] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: '',
    type: 'general',
    subject: '',
    ccAddresses: '',
    bccAddresses: '',
    htmlContent: '',
    description: '',
  });
  const [sending, setSending] = React.useState(false);
  const [sendingForm, setSendingForm] = React.useState({
    templateId: '',
    recipientType: 'all-members',
    recipientUserIds: [],
    recipientFilters: DEFAULT_RECIPIENT_FILTERS,
    sendNow: true,
    scheduledFor: '',
  });
  const [showSendModal, setShowSendModal] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const editorRef = React.useRef(null);
  const [createSendOptions, setCreateSendOptions] = React.useState({
    enabled: false,
    recipientType: 'all-members',
    recipientUserIds: [],
    recipientFilters: DEFAULT_RECIPIENT_FILTERS,
  });

  const templateTypes = ['general', 'event-reminder', 'membership-renewal'];
  const isTriggerTemplateType = (type) => type === 'welcome' || type === 'registration' || type === 'membership-application' || type === 'membership-submission';
  const placeholdersByType = React.useMemo(
    () => ({
      welcome: ['userName', 'clubName'],
      registration: ['userName', 'clubName', 'competitionName', 'competitionEntryFee', 'competitionEntryFeeDisplay', 'teamName', 'registeredByName', 'teamEntryHtml'],
      'membership-application': ['userName', 'clubName', 'applicationType', 'membershipStatus', 'membershipType', 'membershipFee', 'membershipFeeDisplay', 'loginUrl'],
      'membership-submission': ['userName', 'clubName', 'applicationType', 'applicationTypeLabel', 'membershipStatus', 'membershipType', 'membershipFee', 'membershipFeeDisplay', 'profileReference', 'submittedFieldsTable'],
      'membership-renewal': ['userName', 'clubName', 'membershipRenewalDueDate', 'membershipRenewalDueInDays', 'renewUrl'],
      'event-reminder': ['userName', 'clubName', 'competitionName'],
      general: ['userName', 'clubName'],
    }),
    []
  );

  const getRenewalTemplateDraft = () => ({
    name: 'Membership Renewal Reminder',
    type: 'membership-renewal',
    subject: 'Membership renewal due - {{clubName}}',
    htmlContent:
      '<p>Hi {{userName}},</p>' +
      '<p>This is a reminder that your membership with <strong>{{clubName}}</strong> is due for renewal.</p>' +
      '<p><strong>Renewal Due Date:</strong> {{membershipRenewalDueDate}}</p>' +
      '<p><strong>Days Remaining:</strong> {{membershipRenewalDueInDays}}</p>' +
      '<p><a href="{{renewUrl}}">Renew Membership</a></p>' +
      '<p>If you have already renewed, please ignore this message.</p>',
    description: 'Use with filtered members (due soon/overdue).',
  });

  const activePlaceholders = React.useMemo(
    () => placeholdersByType[formData.type] || placeholdersByType.general,
    [formData.type, placeholdersByType]
  );

  React.useEffect(() => {
    if (!showForm || !editorRef.current) return;
    if (editorRef.current.innerHTML !== formData.htmlContent) {
      editorRef.current.innerHTML = formData.htmlContent || '';
    }
  }, [showForm, editingTemplate, formData.htmlContent]);

  // Load templates
  React.useEffect(() => {
    loadTemplates();
    loadClubMembers();
    loadRenewalDue();
  }, [selectedClubId, isSuperAdmin]);

  /* ── Membership renewal reminders ──────────────────────────────────
     The code that sends these existed and nothing called it, so no member
     had ever been reminded. It is not on a timer: a club writing to its own
     members about money decides when that happens. So the club is shown how
     many are due and presses Send.

     The count comes from a dry run of the same code that does the sending,
     rather than a second query that could disagree with it — so the number
     on the button is the number of emails that will actually go out. */
  const [renewalDue, setRenewalDue] = React.useState(null);
  const [renewalSending, setRenewalSending] = React.useState(false);

  const loadRenewalDue = async () => {
    if (isSuperAdmin && !selectedClubId) { setRenewalDue(null); return; }
    try {
      const clubQuery = isSuperAdmin && selectedClubId ? `?clubId=${selectedClubId}` : '';
      setRenewalDue(await apiJson(`/api/emails/renewal-reminders${clubQuery}`));
    } catch {
      /* A count that will not load is not worth a toast on the way into the
         screen; the card says it could not be worked out. */
      setRenewalDue({ unavailable: true });
    }
  };

  const sendRenewalReminders = async () => {
    if (!renewalDue || !renewalDue.count) return;
    const n = renewalDue.count;
    const ok = await showConfirm(
      `Send a renewal reminder to ${n} member${n === 1 ? '' : 's'}?\n\n`
      + 'Each one gets an email saying when their membership is due and a link to renew. '
      + 'Nobody is written to twice for the same renewal.',
    );
    if (!ok) return;
    setRenewalSending(true);
    try {
      const r = await apiJson('/api/emails/renewal-reminders', {
        method: 'POST',
        body: isSuperAdmin && selectedClubId ? { clubId: selectedClubId } : {},
      });
      showToast(r.message || `Reminder sent to ${r.sent} member(s)`, r.failed ? 'amber' : 'green');
      loadRenewalDue();
    } catch (err) {
      showToast(err.message || 'Could not send the reminders', 'red');
    } finally {
      setRenewalSending(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      if (isSuperAdmin && !selectedClubId) {
        setTemplates([]);
        return;
      }
      const token = localStorage.getItem('greensheet_token');
      const clubQuery = isSuperAdmin && selectedClubId ? `?clubId=${selectedClubId}` : '';
      const res = await fetch(`${API}/api/emails/templates${clubQuery}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        setTemplates(await res.json());
      }
    } catch (err) {
      showToast('Failed to load templates', 'red');
    } finally {
      setLoading(false);
    }
  };

  const loadClubMembers = async () => {
    try {
      if (isSuperAdmin && !selectedClubId) {
        setClubMembers([]);
        return;
      }
      const token = localStorage.getItem('greensheet_token');
      const res = await fetch(`${API}/api/auth/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return;
      const users = await res.json();
      const scoped = isSuperAdmin
        ? users.filter(u => String(u.ClubId) === String(selectedClubId))
        : users;
      setClubMembers(scoped.filter(u => !!u.email));
    } catch {
      setClubMembers([]);
    }
  };

  const uniqueMembershipTypes = React.useMemo(
    () => [...new Set(clubMembers.map(m => String(m.membershipType || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [clubMembers]
  );

  const uniquePlayerTypes = React.useMemo(
    () => [...new Set(clubMembers.map(m => String(m.playerType || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [clubMembers]
  );

  const applyMemberFilters = (members, filters) => {
    const src = filters || DEFAULT_RECIPIENT_FILTERS;
    const now = new Date();
    const dueSoonDays = parseInt(src.dueSoonDays, 10) || 30;
    const dueSoonTo = new Date(now);
    dueSoonTo.setDate(dueSoonTo.getDate() + Math.max(1, Math.min(365, dueSoonDays)));

    return members.filter(m => {
      if (!m || !m.email) return false;
      if (Array.isArray(src.membershipTypes) && src.membershipTypes.length > 0 && !src.membershipTypes.includes(String(m.membershipType || ''))) return false;
      if (Array.isArray(src.playerTypes) && src.playerTypes.length > 0 && !src.playerTypes.includes(String(m.playerType || ''))) return false;
      if (Array.isArray(src.accountStatuses) && src.accountStatuses.length > 0 && !src.accountStatuses.includes(String(m.accountStatus || ''))) return false;
      if (src.financialState === 'financial' && !m.isFinancial) return false;
      if (src.financialState === 'non-financial' && m.isFinancial) return false;

      const due = m.membershipRenewalDueAt ? new Date(m.membershipRenewalDueAt) : null;
      const hasDue = !!(due && !Number.isNaN(due.getTime()));
      if (src.renewalState === 'overdue' && (!hasDue || due >= now)) return false;
      if (src.renewalState === 'due-soon' && (!hasDue || due < now || due > dueSoonTo)) return false;
      if (src.renewalState === 'no-due-date' && hasDue) return false;
      return true;
    });
  };

  const filteredCountPreview = sendingForm.recipientType === 'filtered-members'
    ? applyMemberFilters(clubMembers, sendingForm.recipientFilters).length
    : 0;

  const saveTemplate = async () => {
    if (!formData.name || !formData.subject || !formData.htmlContent) {
      return showToast('Name, subject, and HTML content are required', 'orange');
    }
    if (!editingTemplate && createSendOptions.enabled && createSendOptions.recipientType === 'specific-users' && createSendOptions.recipientUserIds.length === 0) {
      return showToast('Select at least one club member', 'orange');
    }
    if (!editingTemplate && createSendOptions.enabled && createSendOptions.recipientType === 'filtered-members') {
      const matched = applyMemberFilters(clubMembers, createSendOptions.recipientFilters || DEFAULT_RECIPIENT_FILTERS);
      if (matched.length === 0) {
        return showToast('No members match the selected filters', 'orange');
      }
    }

    try {
      const token = localStorage.getItem('greensheet_token');
      const method = editingTemplate ? 'PUT' : 'POST';
      const url = editingTemplate
        ? `${API}/api/emails/templates/${editingTemplate.id}`
        : `${API}/api/emails/templates`;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          ...(isSuperAdmin && selectedClubId ? { clubId: selectedClubId } : {}),
        }),
      });

      if (res.ok) {
        const saved = await res.json();
        if (editingTemplate) {
          setTemplates(prev => prev.map(t => t.id === saved.id ? saved : t));
          showToast('Template updated', 'green');
        } else {
          setTemplates(prev => [...prev, saved]);
          if (createSendOptions.enabled) {
            try {
              const sendPayload = {
                templateId: saved.id,
                recipientType: createSendOptions.recipientType,
                sendNow: true,
              };
              if (createSendOptions.recipientType === 'specific-users') {
                sendPayload.recipientUserIds = createSendOptions.recipientUserIds;
              } else if (createSendOptions.recipientType === 'filtered-members') {
                sendPayload.recipientFilters = createSendOptions.recipientFilters;
              }

              const sendRes = await fetch(`${API}/api/emails/send`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(sendPayload),
              });

              if (sendRes.ok) {
                const sendData = await sendRes.json();
                showToast(`Template created. ${sendData.message}`, 'green');
              } else {
                const sendErr = await sendRes.json();
                showToast(`Template created, but send failed: ${sendErr.error || 'Unknown error'}`, 'orange');
              }
            } catch {
              showToast('Template created, but send failed', 'orange');
            }
          } else {
            showToast('Template created', 'green');
          }
        }
        resetForm();
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to save template', 'red');
      }
    } catch (err) {
      showToast('Error saving template', 'red');
    }
  };

  const deleteTemplate = async (id) => {
    if (!await showConfirm('Delete this template?')) return;
    
    try {
      const token = localStorage.getItem('greensheet_token');
      const res = await fetch(`${API}/api/emails/templates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        setTemplates(prev => prev.filter(t => t.id !== id));
        showToast('Template deleted', 'green');
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to delete template', 'red');
      }
    } catch (err) {
      showToast('Error deleting template', 'red');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      type: 'general',
      subject: '',
      ccAddresses: '',
      bccAddresses: '',
      htmlContent: '',
      description: '',
    });
    setCreateSendOptions({ enabled: false, recipientType: 'all-members', recipientUserIds: [], recipientFilters: { ...DEFAULT_RECIPIENT_FILTERS } });
  };

  const startEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject,
      ccAddresses: template.ccAddresses || '',
      bccAddresses: template.bccAddresses || '',
      htmlContent: template.htmlContent,
      description: template.description || '',
    });
    setCreateSendOptions({ enabled: false, recipientType: 'all-members', recipientUserIds: [], recipientFilters: { ...DEFAULT_RECIPIENT_FILTERS } });
    setShowForm(true);
  };

  const startRenewalPreset = () => {
    setEditingTemplate(null);
    setFormData(getRenewalTemplateDraft());
    setCreateSendOptions({ enabled: false, recipientType: 'all-members', recipientUserIds: [], recipientFilters: { ...DEFAULT_RECIPIENT_FILTERS } });
    setShowForm(true);
  };

  const startSend = (template) => {
    setSelectedTemplate(template);
    setSendingForm({
      templateId: template.id,
      recipientType: 'all-members',
      recipientUserIds: [],
      recipientFilters: { ...DEFAULT_RECIPIENT_FILTERS },
      sendNow: true,
      scheduledFor: '',
    });
    setShowSendModal(true);
  };

  const applyEditorCommand = (command, value = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    setFormData(f => ({ ...f, htmlContent: editorRef.current.innerHTML }));
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL (include https://)');
    if (!url) return;
    applyEditorCommand('createLink', url);
  };

  const handleEditorInput = () => {
    if (!editorRef.current) return;
    setFormData(f => ({ ...f, htmlContent: editorRef.current.innerHTML }));
  };

  const sendEmail = async () => {
    if (!sendingForm.templateId) {
      return showToast('Please select a template', 'orange');
    }

    if (!sendingForm.sendNow && !sendingForm.scheduledFor) {
      return showToast('Please select a send time or choose "Send Now"', 'orange');
    }

    if (sendingForm.recipientType === 'specific-users' && (!Array.isArray(sendingForm.recipientUserIds) || sendingForm.recipientUserIds.length === 0)) {
      return showToast('Select at least one club member', 'orange');
    }
    if (sendingForm.recipientType === 'filtered-members' && filteredCountPreview === 0) {
      return showToast('No members match the selected filters', 'orange');
    }

    try {
      setSending(true);
      const token = localStorage.getItem('greensheet_token');
      const payload = {
        templateId: sendingForm.templateId,
        recipientType: sendingForm.recipientType,
        sendNow: sendingForm.sendNow,
      };
      if (sendingForm.recipientType === 'specific-users') {
        payload.recipientUserIds = sendingForm.recipientUserIds;
      } else if (sendingForm.recipientType === 'filtered-members') {
        payload.recipientFilters = sendingForm.recipientFilters;
      }
      if (!sendingForm.sendNow) {
        payload.scheduledFor = sendingForm.scheduledFor;
      }

      const res = await fetch(`${API}/api/emails/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        showToast(data.message || 'Email sent successfully', 'green');
        setShowSendModal(false);
        setSendingForm({ templateId: '', recipientType: 'all-members', recipientUserIds: [], recipientFilters: { ...DEFAULT_RECIPIENT_FILTERS }, sendNow: true, scheduledFor: '' });
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to send email', 'red');
      }
    } catch (err) {
      showToast('Error sending email', 'red');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {isSuperAdmin && !selectedClubId && (
        <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-amber w3-round" style={{ marginTop: 0 }}>
          <strong>Select a club first.</strong> Use the club selector at the top of the app, then open this tab again.
        </div>
      )}
      {/* Renewal reminders — a thing the club sends, not a thing that happens */}
      {renewalDue && !renewalDue.unavailable && (
        <div className="gs-panel w3-margin-bottom">
          <Section
            label="Membership renewal reminders"
            action={(
              <button className="w3-button w3-round"
                style={{
                  minHeight: 'var(--gs-control-md)',
                  background: renewalDue.count ? 'var(--gs-action-fill, var(--club-10))' : undefined,
                  color: renewalDue.count ? '#fff' : undefined,
                  fontWeight: 600,
                }}
                disabled={!renewalDue.count || !renewalDue.emailConfigured || renewalSending}
                onClick={sendRenewalReminders}>
                {renewalSending
                  ? <><i className="fa fa-spinner fa-spin w3-margin-right" aria-hidden="true"></i>Sending…</>
                  : renewalDue.count
                    ? `Send ${renewalDue.count} reminder${renewalDue.count === 1 ? '' : 's'}`
                    : 'Nothing due'}
              </button>
            )}>
            {renewalDue.count > 0 ? (
              <p style={{ margin: 0, fontSize: 'var(--gs-text-sm)' }}>
                <strong>{renewalDue.count} member{renewalDue.count === 1 ? ' is' : 's are'}</strong>
                {' '}due to renew within the next {renewalDue.windowDays} days and
                {' '}{renewalDue.count === 1 ? 'has' : 'have'} not been reminded yet.
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-muted)' }}>
                Nobody is due a reminder in the next {renewalDue.windowDays} days. Anyone
                already reminded for this renewal is not counted again.
              </p>
            )}

            {renewalDue.count > 0 && (
              <p style={{ margin: 'var(--gs-space-2) 0 0', fontSize: 'var(--gs-text-xs)', color: 'var(--gs-text-muted)' }}>
                {renewalDue.recipients.slice(0, 6).map(r => r.name || r.email).join(', ')}
                {renewalDue.count > 6 && ` and ${renewalDue.count - 6} more`}
              </p>
            )}

            {!renewalDue.emailConfigured && (
              <p style={{ margin: 'var(--gs-space-3) 0 0', fontSize: 'var(--gs-text-xs)', color: 'var(--gs-text-muted)' }}>
                <i className="fa fa-exclamation-triangle w3-margin-right" aria-hidden="true"></i>
                Outgoing email is not set up on this server, so nothing can be sent yet.
              </p>
            )}

            <p style={{ margin: 'var(--gs-space-3) 0 0', fontSize: 'var(--gs-text-xs)', color: 'var(--gs-text-faint)' }}>
              These are only ever sent when you press the button — nothing goes out on its own.
            </p>
          </Section>
        </div>
      )}

      <div className="gs-panel w3-margin-bottom">
        <div className="gs-tabs" style={{ borderBottom: 'none', paddingBottom: 'var(--gs-space-4)' }}>
          <span className="gs-section-label">Email templates ({templates.length})</span>
          <span className="gs-tabs-end">
            <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-sm)' }}
              onClick={startRenewalPreset} disabled={showForm}>
              Renewal preset
            </button>
            <button className="w3-button w3-round"
              style={{ minHeight: 'var(--gs-control-sm)', background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600 }}
              onClick={() => setShowForm(true)} disabled={showForm}>
              New template
            </button>
          </span>
        </div>

        {/* Template Form */}
        {showForm && (
          <div className="w3-container w3-padding" style={{ background: 'var(--gs-surface-soft)', border: '1px solid var(--gs-border)', borderRadius: 'var(--gs-radius-md)', marginBottom: 'var(--gs-space-6)' }}>
            <h4>{editingTemplate ? 'Edit Template' : 'Create New Template'}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="w3-small"><strong>Template Name *</strong></label>
                <input className="w3-input w3-border w3-round w3-margin-top-small" placeholder="e.g., Welcome Email"
                  value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="w3-small"><strong>Type *</strong></label>
                <select
                  className="w3-select w3-border w3-round w3-margin-top-small"
                  value={formData.type}
                  disabled={editingTemplate && isTriggerTemplateType(formData.type)}
                  onChange={e => {
                    const nextType = e.target.value;
                    setFormData(f => {
                      if (!editingTemplate && nextType === 'membership-renewal') {
                        const preset = getRenewalTemplateDraft();
                        return {
                          ...f,
                          type: nextType,
                          subject: f.subject || preset.subject,
                          htmlContent: f.htmlContent || preset.htmlContent,
                          description: f.description || preset.description,
                          name: f.name || preset.name,
                        };
                      }
                      return { ...f, type: nextType };
                    });
                  }}
                >
                  {editingTemplate && isTriggerTemplateType(formData.type)
                    ? <option value={formData.type}>{formData.type}</option>
                    : templateTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="w3-margin-bottom">
              <label className="w3-small"><strong>Email Subject *</strong></label>
              <input className="w3-input w3-border w3-round w3-margin-top-small" placeholder="Email subject line"
                value={formData.subject} onChange={e => setFormData(f => ({ ...f, subject: e.target.value }))} />
              <p className="w3-small w3-text-grey" style={{ marginTop: '8px', marginBottom: 0 }}>
                You can use placeholders in subject and body: {activePlaceholders.map((p, i) => `${i > 0 ? ', ' : ''}{{${p}}}`)}
              </p>
            </div>

            {/* Standing copies. Kept together under the subject, where the
                other addressing lives, and said plainly: these go on every
                message the template sends, so a mailout to two hundred
                members copies them two hundred times. */}
            <div className="w3-margin-bottom">
              <div className="w3-row-padding" style={{ margin: '0 -8px' }}>
                <div className="w3-half">
                  <label className="w3-small"><strong>CC</strong></label>
                  <input className="w3-input w3-border w3-round w3-margin-top-small"
                    placeholder="secretary@club.nz, treasurer@club.nz"
                    value={formData.ccAddresses}
                    onChange={e => setFormData(f => ({ ...f, ccAddresses: e.target.value }))} />
                </div>
                <div className="w3-half">
                  <label className="w3-small"><strong>BCC</strong></label>
                  <input className="w3-input w3-border w3-round w3-margin-top-small"
                    placeholder="records@club.nz"
                    value={formData.bccAddresses}
                    onChange={e => setFormData(f => ({ ...f, bccAddresses: e.target.value }))} />
                </div>
              </div>
              <p className="w3-small w3-text-grey" style={{ marginTop: '8px', marginBottom: 0 }}>
                Optional, and separate addresses with a comma. Everyone listed is copied on
                every message this template sends — on a mailout to the whole club, that is
                one copy per member. CC is visible to the recipient; BCC is not.
              </p>
            </div>
            <div className="w3-margin-bottom">
              <label className="w3-small"><strong>Email Body (HTML) *</strong></label>
              <div className="w3-border w3-round w3-margin-top-small" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '8px', borderBottom: '1px solid #ddd', background: '#fff', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  <button type="button" className="w3-button w3-small w3-theme-l4 w3-round" onClick={() => applyEditorCommand('bold')}><i className="fa fa-bold"></i></button>
                  <button type="button" className="w3-button w3-small w3-theme-l4 w3-round" onClick={() => applyEditorCommand('italic')}><i className="fa fa-italic"></i></button>
                  <button type="button" className="w3-button w3-small w3-theme-l4 w3-round" onClick={() => applyEditorCommand('underline')}><i className="fa fa-underline"></i></button>
                  <button type="button" className="w3-button w3-small w3-theme-l4 w3-round" onClick={() => applyEditorCommand('insertUnorderedList')}><i className="fa fa-list-ul"></i></button>
                  <button type="button" className="w3-button w3-small w3-theme-l4 w3-round" onClick={() => applyEditorCommand('insertOrderedList')}><i className="fa fa-list-ol"></i></button>
                  <button type="button" className="w3-button w3-small w3-theme-l4 w3-round" onClick={insertLink}><i className="fa fa-link"></i></button>
                  <button type="button" className="w3-button w3-small w3-theme-l4 w3-round" onClick={() => applyEditorCommand('removeFormat')}><i className="fa fa-eraser"></i></button>
                </div>
                <div
                  ref={editorRef}
                  className="w3-padding"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleEditorInput}
                  style={{ minHeight: '260px', background: '#fff', outline: 'none', whiteSpace: 'normal' }}
                />
              </div>
              <p className="w3-small w3-text-grey" style={{ marginTop: '8px' }}>
                Use the toolbar to format text visually. HTML is generated automatically.
              </p>
              <p className="w3-small w3-text-grey" style={{ marginTop: '6px', marginBottom: 0 }}>
                Example: Hi {'{{userName}}'}, welcome to {'{{clubName}}'}.
              </p>
            </div>
            <div className="w3-margin-bottom">
              <label className="w3-small"><strong>Description (optional)</strong></label>
              <textarea className="w3-input w3-border w3-round w3-margin-top-small" style={{ minHeight: '80px', resize: 'vertical' }}
                placeholder="Notes about this template..."
                value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} />
            </div>

            {!editingTemplate && !isTriggerTemplateType(formData.type) && (
              <div className="w3-panel w3-pale-blue w3-leftbar w3-border-blue w3-round" style={{ marginTop: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={createSendOptions.enabled}
                    onChange={(e) => setCreateSendOptions((prev) => ({ ...prev, enabled: e.target.checked }))}
                    style={{ marginRight: '8px' }}
                  />
                  <strong>Choose recipients now and send after saving</strong>
                </label>

                {createSendOptions.enabled && (
                  <>
                    <div className="w3-margin-bottom">
                      <label className="w3-small"><strong>Send To</strong></label>
                      <select
                        className="w3-select w3-border w3-round w3-margin-top-small"
                        value={createSendOptions.recipientType}
                        onChange={(e) => setCreateSendOptions((prev) => ({
                          ...prev,
                          recipientType: e.target.value,
                          recipientUserIds: e.target.value === 'specific-users' ? prev.recipientUserIds : [],
                          recipientFilters: e.target.value === 'filtered-members' ? (prev.recipientFilters || { ...DEFAULT_RECIPIENT_FILTERS }) : { ...DEFAULT_RECIPIENT_FILTERS },
                        }))}
                      >
                        <option value="all-members">All Club Members</option>
                        <option value="specific-users">Specific Users</option>
                        <option value="filtered-members">Filtered Members</option>
                      </select>
                    </div>

                    {createSendOptions.recipientType === 'specific-users' && (
                      <div className="w3-margin-bottom">
                        <label className="w3-small"><strong>Select Club Members</strong></label>
                        <div className="w3-border w3-round" style={{ maxHeight: '180px', overflowY: 'auto', padding: '8px', marginTop: '6px', background: '#fff' }}>
                          {clubMembers.length === 0 ? (
                            <p className="w3-small w3-text-grey" style={{ margin: 0 }}>No club members with email found.</p>
                          ) : (
                            clubMembers.map(member => (
                              <label key={member.id} style={{ display: 'block', marginBottom: '6px' }}>
                                <input
                                  type="checkbox"
                                  checked={createSendOptions.recipientUserIds.includes(member.id)}
                                  onChange={(e) => {
                                    setCreateSendOptions((prev) => ({
                                      ...prev,
                                      recipientUserIds: e.target.checked
                                        ? [...prev.recipientUserIds, member.id]
                                        : prev.recipientUserIds.filter(id => id !== member.id),
                                    }));
                                  }}
                                  style={{ marginRight: '8px' }}
                                />
                                <span>{member.name} ({member.email})</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    )}

                    {createSendOptions.recipientType === 'filtered-members' && (
                      <div className="w3-margin-bottom">
                        <label className="w3-small"><strong>Recipient Filters</strong></label>
                        <div className="w3-border w3-round w3-padding-small" style={{ marginTop: '6px', background: '#fff' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div>
                              <label className="w3-tiny w3-text-grey">Membership Type</label>
                              <select className="w3-select w3-border w3-round" multiple size={3}
                                value={createSendOptions.recipientFilters.membershipTypes || []}
                                onChange={e => setCreateSendOptions(prev => ({
                                  ...prev,
                                  recipientFilters: {
                                    ...prev.recipientFilters,
                                    membershipTypes: Array.from(e.target.selectedOptions).map(o => o.value),
                                  },
                                }))}>
                                {uniqueMembershipTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="w3-tiny w3-text-grey">Player Type</label>
                              <select className="w3-select w3-border w3-round" multiple size={3}
                                value={createSendOptions.recipientFilters.playerTypes || []}
                                onChange={e => setCreateSendOptions(prev => ({
                                  ...prev,
                                  recipientFilters: {
                                    ...prev.recipientFilters,
                                    playerTypes: Array.from(e.target.selectedOptions).map(o => o.value),
                                  },
                                }))}>
                                {uniquePlayerTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="w3-tiny w3-text-grey">Account Status</label>
                              <select className="w3-select w3-border w3-round" multiple size={3}
                                value={createSendOptions.recipientFilters.accountStatuses || []}
                                onChange={e => setCreateSendOptions(prev => ({
                                  ...prev,
                                  recipientFilters: {
                                    ...prev.recipientFilters,
                                    accountStatuses: Array.from(e.target.selectedOptions).map(o => o.value),
                                  },
                                }))}>
                                <option value="verified">verified</option>
                                <option value="unverified">unverified</option>
                                <option value="blocked">blocked</option>
                              </select>
                            </div>
                            <div>
                              <label className="w3-tiny w3-text-grey">Financial Status</label>
                              <select className="w3-select w3-border w3-round"
                                value={createSendOptions.recipientFilters.financialState || 'all'}
                                onChange={e => setCreateSendOptions(prev => ({
                                  ...prev,
                                  recipientFilters: { ...prev.recipientFilters, financialState: e.target.value },
                                }))}>
                                <option value="all">All</option>
                                <option value="financial">Financial only</option>
                                <option value="non-financial">Non-financial only</option>
                              </select>
                            </div>
                            <div>
                              <label className="w3-tiny w3-text-grey">Renewal State</label>
                              <select className="w3-select w3-border w3-round"
                                value={createSendOptions.recipientFilters.renewalState || 'all'}
                                onChange={e => setCreateSendOptions(prev => ({
                                  ...prev,
                                  recipientFilters: { ...prev.recipientFilters, renewalState: e.target.value },
                                }))}>
                                <option value="all">All</option>
                                <option value="due-soon">Due soon</option>
                                <option value="overdue">Overdue</option>
                                <option value="no-due-date">No due date</option>
                              </select>
                            </div>
                            {(createSendOptions.recipientFilters.renewalState || 'all') === 'due-soon' && (
                              <div>
                                <label className="w3-tiny w3-text-grey">Due Soon Window (days)</label>
                                <input className="w3-input w3-border w3-round" type="number" min={1} max={365}
                                  value={createSendOptions.recipientFilters.dueSoonDays || 30}
                                  onChange={e => setCreateSendOptions(prev => ({
                                    ...prev,
                                    recipientFilters: {
                                      ...prev.recipientFilters,
                                      dueSoonDays: parseInt(e.target.value, 10) || 30,
                                    },
                                  }))} />
                              </div>
                            )}
                          </div>
                          <p className="w3-small w3-text-grey" style={{ marginBottom: 0, marginTop: '8px' }}>
                            Filters are applied to club members with an email address.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="w3-button w3-theme w3-round" onClick={saveTemplate}>
                <i className="fa fa-save w3-margin-right"></i>Save Template
              </button>
              <button className="w3-button w3-theme-l3 w3-round" onClick={resetForm}>
                <i className="fa fa-times w3-margin-right"></i>Cancel
              </button>
            </div>
          </div>
        )}

        {/* Templates List */}
        {loading ? (
          <div className="w3-center w3-padding"><i className="fa fa-spinner fa-spin fa-2x"></i></div>
        ) : templates.length === 0 ? (
          <p className="w3-center w3-text-grey w3-padding">No templates yet. <button type="button" className="w3-button w3-small w3-theme-l4 w3-round" onClick={() => setShowForm(true)}>Create one</button></p>
        ) : (
          <div className="card-grid">
            {templates.map(template => {
              // An auto-trigger template is sent by the app when something
              // happens, so it can be edited but not sent by hand or deleted.
              const auto = isTriggerTemplateType(template.type);
              return (
                <div key={template.id} className="template-card">
                  <h4 style={{ margin: 0 }}>{template.name}</h4>
                  <div style={{ display: 'flex', gap: 'var(--gs-space-2)', alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                    {/* The type is stored as a slug. Set in caps with wide
                        tracking, "membership-application" reads as noise; the
                        hyphens are an implementation detail. */}
                    <span className="gs-section-label">{String(template.type || '').replace(/-/g, ' ')}</span>
                    {auto && (
                      <span style={{ fontSize: 'var(--gs-text-xs)', color: 'var(--gs-accent-text, var(--club-30))', fontWeight: 600 }}>
                        · Sent automatically
                      </span>
                    )}
                  </div>
                  {template.description && (
                    <p style={{ margin: '10px 0 0', color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
                      {template.description}
                    </p>
                  )}
                  <p style={{ margin: '10px 0 0', fontSize: 'var(--gs-text-sm)' }}>
                    <span style={{ color: 'var(--gs-text-faint)' }}>Subject </span>
                    <span style={{ color: 'var(--gs-text)' }}>{template.subject}</span>
                  </p>
                  {/* On the card rather than only inside the editor: who else
                      gets a copy is the sort of thing worth noticing before
                      pressing Send, not after. */}
                  {(template.ccAddresses || template.bccAddresses) && (
                    <p style={{ margin: '4px 0 0', fontSize: 'var(--gs-text-sm)' }}>
                      {template.ccAddresses && (
                        <>
                          <span style={{ color: 'var(--gs-text-faint)' }}>CC </span>
                          <span style={{ color: 'var(--gs-text)' }}>{template.ccAddresses}</span>
                        </>
                      )}
                      {template.ccAddresses && template.bccAddresses && (
                        <span style={{ color: 'var(--gs-text-faint)' }}> · </span>
                      )}
                      {template.bccAddresses && (
                        <>
                          <span style={{ color: 'var(--gs-text-faint)' }}>BCC </span>
                          <span style={{ color: 'var(--gs-text)' }}>{template.bccAddresses}</span>
                        </>
                      )}
                    </p>
                  )}
                  <div className="template-card-actions">
                    <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-sm)' }}
                      onClick={() => startEdit(template)}>
                      Edit
                    </button>
                    {!auto && (
                      <button className="w3-button w3-round"
                        style={{ minHeight: 'var(--gs-control-sm)', background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600 }}
                        onClick={() => startSend(template)}>
                        Send
                      </button>
                    )}
                    {!auto && (
                      <button className="w3-button w3-round w3-hover-red"
                        style={{ minHeight: 'var(--gs-control-sm)', color: 'var(--gs-text-faint)', marginLeft: 'auto' }}
                        onClick={() => deleteTemplate(template.id)}
                        title={`Delete ${template.name}`} aria-label={`Delete ${template.name}`}>
                        <i className="fa fa-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Send Email Modal */}
      {showSendModal && selectedTemplate && (
        <div className="w3-modal" style={{ display: 'block' }}>
          <div className="w3-modal-content w3-card w3-round w3-animate-top" style={{ maxWidth: '500px' }}>
            <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700 }}>Send Email: {selectedTemplate.name}</span>
              <button className="w3-button w3-round w3-hover-white" onClick={() => setShowSendModal(false)}>&times;</button>
            </div>
            <div className="w3-container w3-padding">
              <div className="w3-margin-bottom">
                <label className="w3-small"><strong>Send To</strong></label>
                <select className="w3-select w3-border w3-round w3-margin-top-small" value={sendingForm.recipientType}
                  onChange={e => setSendingForm(f => ({
                    ...f,
                    recipientType: e.target.value,
                    recipientUserIds: e.target.value === 'specific-users' ? f.recipientUserIds : [],
                    recipientFilters: e.target.value === 'filtered-members' ? (f.recipientFilters || { ...DEFAULT_RECIPIENT_FILTERS }) : { ...DEFAULT_RECIPIENT_FILTERS },
                  }))}>
                  <option value="all-members">All Club Members</option>
                  <option value="specific-users">Specific Users</option>
                  <option value="filtered-members">Filtered Members</option>
                </select>
              </div>

              {sendingForm.recipientType === 'specific-users' && (
                <div className="w3-margin-bottom">
                  <label className="w3-small"><strong>Select Club Members</strong></label>
                  <div className="w3-border w3-round" style={{ maxHeight: '180px', overflowY: 'auto', padding: '8px', marginTop: '6px' }}>
                    {clubMembers.length === 0 ? (
                      <p className="w3-small w3-text-grey" style={{ margin: 0 }}>No club members with email found.</p>
                    ) : (
                      clubMembers.map(member => (
                        <label key={member.id} style={{ display: 'block', marginBottom: '6px' }}>
                          <input
                            type="checkbox"
                            checked={sendingForm.recipientUserIds.includes(member.id)}
                            onChange={(e) => {
                              setSendingForm((f) => ({
                                ...f,
                                recipientUserIds: e.target.checked
                                  ? [...f.recipientUserIds, member.id]
                                  : f.recipientUserIds.filter(id => id !== member.id),
                              }));
                            }}
                            style={{ marginRight: '8px' }}
                          />
                          <span>{member.name} ({member.email})</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {sendingForm.recipientType === 'filtered-members' && (
                <div className="w3-margin-bottom">
                  <label className="w3-small"><strong>Recipient Filters</strong></label>
                  <div className="w3-border w3-round w3-padding-small" style={{ marginTop: '6px', background: '#fff' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div>
                        <label className="w3-tiny w3-text-grey">Membership Type</label>
                        <select className="w3-select w3-border w3-round" multiple size={3}
                          value={sendingForm.recipientFilters.membershipTypes || []}
                          onChange={e => setSendingForm(f => ({
                            ...f,
                            recipientFilters: {
                              ...f.recipientFilters,
                              membershipTypes: Array.from(e.target.selectedOptions).map(o => o.value),
                            },
                          }))}>
                          {uniqueMembershipTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="w3-tiny w3-text-grey">Player Type</label>
                        <select className="w3-select w3-border w3-round" multiple size={3}
                          value={sendingForm.recipientFilters.playerTypes || []}
                          onChange={e => setSendingForm(f => ({
                            ...f,
                            recipientFilters: {
                              ...f.recipientFilters,
                              playerTypes: Array.from(e.target.selectedOptions).map(o => o.value),
                            },
                          }))}>
                          {uniquePlayerTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="w3-tiny w3-text-grey">Account Status</label>
                        <select className="w3-select w3-border w3-round" multiple size={3}
                          value={sendingForm.recipientFilters.accountStatuses || []}
                          onChange={e => setSendingForm(f => ({
                            ...f,
                            recipientFilters: {
                              ...f.recipientFilters,
                              accountStatuses: Array.from(e.target.selectedOptions).map(o => o.value),
                            },
                          }))}>
                          <option value="verified">verified</option>
                          <option value="unverified">unverified</option>
                          <option value="blocked">blocked</option>
                        </select>
                      </div>
                      <div>
                        <label className="w3-tiny w3-text-grey">Financial Status</label>
                        <select className="w3-select w3-border w3-round"
                          value={sendingForm.recipientFilters.financialState || 'all'}
                          onChange={e => setSendingForm(f => ({
                            ...f,
                            recipientFilters: { ...f.recipientFilters, financialState: e.target.value },
                          }))}>
                          <option value="all">All</option>
                          <option value="financial">Financial only</option>
                          <option value="non-financial">Non-financial only</option>
                        </select>
                      </div>
                      <div>
                        <label className="w3-tiny w3-text-grey">Renewal State</label>
                        <select className="w3-select w3-border w3-round"
                          value={sendingForm.recipientFilters.renewalState || 'all'}
                          onChange={e => setSendingForm(f => ({
                            ...f,
                            recipientFilters: { ...f.recipientFilters, renewalState: e.target.value },
                          }))}>
                          <option value="all">All</option>
                          <option value="due-soon">Due soon</option>
                          <option value="overdue">Overdue</option>
                          <option value="no-due-date">No due date</option>
                        </select>
                      </div>
                      {(sendingForm.recipientFilters.renewalState || 'all') === 'due-soon' && (
                        <div>
                          <label className="w3-tiny w3-text-grey">Due Soon Window (days)</label>
                          <input className="w3-input w3-border w3-round" type="number" min={1} max={365}
                            value={sendingForm.recipientFilters.dueSoonDays || 30}
                            onChange={e => setSendingForm(f => ({
                              ...f,
                              recipientFilters: {
                                ...f.recipientFilters,
                                dueSoonDays: parseInt(e.target.value, 10) || 30,
                              },
                            }))} />
                        </div>
                      )}
                    </div>
                    <p className="w3-small" style={{ marginTop: '8px', marginBottom: 0 }}>
                      Matching recipients: <strong>{filteredCountPreview}</strong>
                    </p>
                  </div>
                </div>
              )}

              <div className="w3-margin-bottom">
                <label className="w3-small"><strong>When to Send</strong></label>
                <div style={{ marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <input type="radio" name="sendTime" value="now" checked={sendingForm.sendNow}
                      onChange={() => setSendingForm(f => ({ ...f, sendNow: true }))} style={{ marginRight: '8px' }} />
                    <span>Send Immediately</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <input type="radio" name="sendTime" value="schedule" checked={!sendingForm.sendNow}
                      onChange={() => setSendingForm(f => ({ ...f, sendNow: false }))} style={{ marginRight: '8px' }} />
                    <span>Schedule for Later</span>
                  </label>
                </div>
              </div>

              {!sendingForm.sendNow && (
                <div className="w3-margin-bottom">
                  <label className="w3-small"><strong>Schedule Date &amp; Time *</strong></label>
                  <input type="datetime-local" className="w3-input w3-border w3-round w3-margin-top-small"
                    value={sendingForm.scheduledFor}
                    onChange={e => setSendingForm(f => ({ ...f, scheduledFor: e.target.value }))} />
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button className="w3-button w3-theme w3-round" onClick={sendEmail} disabled={sending}>
                  {sending ? <i className="fa fa-spinner fa-spin w3-margin-right"></i> : <i className="fa fa-paper-plane w3-margin-right"></i>}
                  Send Email
                </button>
                <button className="w3-button w3-theme-l3 w3-round" onClick={() => setShowSendModal(false)} disabled={sending}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailTemplatesPanel;
